<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    profile: { type: Object, required: true },
});

const logoError = ref(false);

const domain = computed(() => {
    const website = props.profile.job_company_website || '';
    try {
        if (website.includes('://')) {
            return new URL(website).hostname;
        }
        return website.replace(/^www\./, '');
    } catch {
        return website.replace(/^www\./, '');
    }
});

const logoUrl = computed(() => {
    if (!domain.value) return null;
    return `https://img.logo.dev/${domain.value}?token=pk_a]JjY0MDEtYjc3OS00NzRiLTkwYTQtNjg2NWNlMzZmMGNk&size=40&format=png`;
});

const initials = computed(() => {
    const name = props.profile.job_company_name || '';
    return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
});

const initialsColor = computed(() => {
    const colors = [
        'bg-purple-100 text-purple-700',
        'bg-blue-100 text-blue-700',
        'bg-green-100 text-green-700',
        'bg-amber-100 text-amber-700',
        'bg-rose-100 text-rose-700',
        'bg-cyan-100 text-cyan-700',
    ];
    const name = props.profile.full_name || '';
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
});
</script>

<template>
    <div class="flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-gray-200">
        <img
            v-if="logoUrl && !logoError"
            :src="logoUrl"
            @error="logoError = true"
            class="w-8 h-8 rounded-md border border-gray-100 flex-shrink-0 object-contain bg-white"
            :alt="profile.job_company_name"
        />
        <div
            v-else
            class="w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center text-xs font-semibold"
            :class="initialsColor"
        >
            {{ initials }}
        </div>
        <div class="min-w-0">
            <p class="text-xs font-medium text-gray-900 truncate mb-0">{{ profile.full_name }}</p>
            <p class="text-[11px] text-gray-500 truncate mb-0">{{ profile.job_title }}</p>
            <p class="text-[10px] text-gray-400 truncate mb-0">
                {{ profile.job_company_name }}
                <span v-if="profile.location_name"> &middot; {{ profile.location_name }}</span>
            </p>
        </div>
    </div>
</template>
